import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Trash2, Copy, CheckCircle2, ClipboardList, Send } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import {
  createIntakeLinkSchema,
  type CreateIntakeLinkInput,
} from "@shared/validation/intake.schema";
import { EXPIRATION_PRESETS, type ExpirationPreset } from "@shared/validation/sharedLink.schema";
import {
  useIntakeLinks,
  useCreateIntakeLink,
  useRevokeIntakeLink,
  type IntakeLinkSummary,
} from "../../lib/api/useIntakeLinks";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { INPUT_CLASS } from "../../lib/forms/inputClass";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { DialogShell } from "../ui/DialogShell";
import { Field } from "../ui/Field";
import { SectionCard } from "../ui/SectionCard";
import { IconChip } from "../ui/IconChip";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { Spinner } from "../ui/Spinner";
import { EmptyState } from "../ui/EmptyState";
import { SECTION_ACCENT } from "../../lib/accents";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";

const EXPIRATION_LABELS: Record<ExpirationPreset, string> = {
  "24h": "24 hours",
  "7d": "7 days",
  "30d": "30 days",
  never: "Never expires",
};

const SECTION_LABELS: Array<{
  key: keyof CreateIntakeLinkInput["writableSections"];
  label: string;
}> = [
  { key: "basic", label: "Basic info" },
  { key: "emergencyContacts", label: "Emergency contacts" },
  { key: "allergies", label: "Allergies" },
  { key: "medications", label: "Medications" },
  { key: "carePreferences", label: "Care preferences" },
];

export function IntakeSection({ childId }: { childId: string }) {
  const { data: links, isLoading, error } = useIntakeLinks(childId);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleting, setDeleting] = useState<IntakeLinkSummary | undefined>();
  const revoke = useRevokeIntakeLink(childId);
  const { publish } = useToast();

  const handleRevoke = async () => {
    if (!deleting) return;
    try {
      await revoke.mutateAsync(deleting.id);
      publish({ variant: "success", title: "Intake link revoked" });
      setDeleting(undefined);
    } catch (err) {
      publish({
        variant: "error",
        title: "Couldn't revoke link",
        description: err instanceof ApiError ? err.message : undefined,
      });
      setDeleting(undefined);
    }
  };

  return (
    <section className="space-y-6">
      <SectionCard
        as="div"
        icon={ClipboardList}
        title="Medical Proxy Intake"
        accent={SECTION_ACCENT.intake}
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden /> Send intake link
          </Button>
        }
      >
        <p className="text-sm text-neutral-700">
          Send a one-time link to a medical proxy to fill out this patient&apos;s
          information. You choose which sections they can complete.
        </p>
      </SectionCard>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Spinner size="sm" />
          Loading intake links…
        </div>
      )}
      {error && (
        <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{error.message}</p>
      )}
      {!isLoading && !error && (links?.length ?? 0) === 0 && (
        <EmptyState
          className="p-8"
          icon={Send}
          title="No intake links yet"
          description="Send a link to collect this patient's details from a proxy."
        />
      )}
      {links && links.length > 0 && (
        <ul className="space-y-3">
          {links.map((link) => (
            <IntakeCard key={link.id} link={link} onRevoke={() => setDeleting(link)} />
          ))}
        </ul>
      )}

      <IntakeLinkDialog open={createOpen} onOpenChange={setCreateOpen} childId={childId} />
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(undefined)}
        title="Revoke intake link"
        description={`Revoke this intake link${deleting?.proxyName ? ` for ${deleting.proxyName}` : ""}? It will stop working immediately.`}
        confirmLabel="Revoke"
        destructive
        isPending={revoke.isPending}
        onConfirm={() => {
          void handleRevoke();
        }}
      />
    </section>
  );
}

function statusOf(link: IntakeLinkSummary): { label: string; cls: string } {
  if (link.submittedAt) return { label: "Submitted", cls: "bg-emerald-100 text-emerald-700" };
  if (!link.isActive) return { label: "Revoked", cls: "bg-neutral-200 text-neutral-600" };
  if (link.lastAccessedAt) return { label: "Viewed", cls: "bg-amber-100 text-amber-700" };
  return { label: "Sent", cls: "bg-sky-100 text-sky-700" };
}

function IntakeCard({ link, onRevoke }: { link: IntakeLinkSummary; onRevoke: () => void }) {
  const status = statusOf(link);
  const sections = Object.values(link.writableSections).filter(Boolean).length;
  const totalSections = Object.keys(link.writableSections).length;
  return (
    <Card
      asChild
      className={cn(
        "rounded-2xl p-4",
        link.isActive ? "border-amber-100" : "bg-neutral-50 opacity-75",
      )}
    >
      <li>
        <div className="flex items-start gap-3">
          <IconChip icon={ClipboardList} accent={SECTION_ACCENT.intake} size="sm" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-neutral-900">
                {link.proxyName ?? "Medical proxy"}
              </h4>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${status.cls}`}
              >
                {status.label}
              </span>
            </div>
            {link.proxyEmail && <p className="mt-0.5 text-xs text-neutral-500">{link.proxyEmail}</p>}
            <p className="mt-2 text-[11px] text-neutral-400">
              {sections} of {totalSections} sections · Token: {link.tokenPrefix}…
            </p>
          </div>
          {link.isActive && (
            <button
              type="button"
              onClick={onRevoke}
              aria-label="Revoke intake link"
              className={cn(
                "rounded-full p-1.5 text-neutral-600 transition hover:bg-red-50 hover:text-red-700",
                FOCUS_RING,
              )}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </li>
    </Card>
  );
}

function IntakeLinkDialog({
  open,
  onOpenChange,
  childId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  childId: string;
}) {
  const createMutation = useCreateIntakeLink(childId);
  const { publish } = useToast();
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<CreateIntakeLinkInput>({
    resolver: zodResolver(createIntakeLinkSchema),
    defaultValues: {
      childId,
      proxyName: null,
      proxyEmail: null,
      writableSections: {
        basic: true,
        emergencyContacts: true,
        allergies: true,
        medications: true,
        carePreferences: true,
      },
      expirationPreset: "7d",
      singleUse: true,
    },
  });

  const onSubmit = async (data: CreateIntakeLinkInput) => {
    try {
      const result = await createMutation.mutateAsync(data);
      setCreatedUrl(`${window.location.origin}/intake/${result.token}`);
      publish({ variant: "success", title: "Intake link created" });
    } catch (err) {
      publish({
        variant: "error",
        title: "Couldn't create intake link",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  const handleCopy = async () => {
    if (!createdUrl) return;
    try {
      await navigator.clipboard.writeText(createdUrl);
      setCopied(true);
      publish({ variant: "success", title: "Link copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      publish({ variant: "error", title: "Unable to copy link" });
    }
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setCreatedUrl(null);
      setCopied(false);
      reset();
      createMutation.reset();
    }
    onOpenChange(nextOpen);
  };

  return (
    <DialogShell
      open={open}
      onOpenChange={handleClose}
      size="lg"
      title={createdUrl ? "Intake link created!" : "Send intake link"}
      description={
        createdUrl
          ? "Copy this link and send it to the medical proxy. It is shown only once."
          : "Choose what the proxy can fill out, then share the generated link."
      }
    >
      {createdUrl ? (
        <div className="mt-6 space-y-4">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="break-all font-mono text-xs text-emerald-900">{createdUrl}</p>
          </div>
          <Button className="w-full" onClick={handleCopy}>
            {copied ? (
              <>
                <CheckCircle2 className="h-4 w-4" aria-hidden /> Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" aria-hidden /> Copy link to clipboard
              </>
            )}
          </Button>
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-neutral-100 bg-white p-4 shadow-sm">
            <QRCodeSVG value={createdUrl} size={148} level="M" aria-label="QR code for the intake link" />
            <p className="text-xs text-neutral-500">Or scan to open on a phone</p>
          </div>
          <Button variant="secondary" className="w-full" onClick={() => handleClose(false)}>
            Done
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
          <Field label="Proxy name (optional)" htmlFor="intake-proxy-name">
            <input
              id="intake-proxy-name"
              type="text"
              {...register("proxyName")}
              className={INPUT_CLASS}
              placeholder="e.g., Jane Doe (parent)"
            />
          </Field>
          <Field label="Proxy email (optional)" htmlFor="intake-proxy-email">
            <input
              id="intake-proxy-email"
              type="email"
              {...register("proxyEmail")}
              className={INPUT_CLASS}
              placeholder="jane@example.com"
            />
          </Field>
          <Field label="Link expires" htmlFor="intake-expiration">
            <select id="intake-expiration" {...register("expirationPreset")} className={INPUT_CLASS}>
              {EXPIRATION_PRESETS.map((preset) => (
                <option key={preset} value={preset}>
                  {EXPIRATION_LABELS[preset]}
                </option>
              ))}
            </select>
          </Field>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-neutral-800">
              Sections the proxy can fill
            </legend>
            {SECTION_LABELS.map((s) => (
              <label
                key={s.key}
                className="flex cursor-pointer items-center gap-2.5 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-700 transition hover:border-sky-200 hover:bg-sky-50/60"
              >
                <input
                  type="checkbox"
                  {...register(`writableSections.${s.key}`)}
                  className="h-4 w-4 rounded border-neutral-300 accent-sky-600"
                />
                {s.label}
              </label>
            ))}
          </fieldset>

          {createMutation.error instanceof ApiError && (
            <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
              {createMutation.error.message}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isSubmitting || createMutation.isPending}>
              {createMutation.isPending ? "Creating…" : "Create intake link"}
            </Button>
          </div>
        </form>
      )}
    </DialogShell>
  );
}
